import { useState, useCallback } from 'react'
import {
  getSelectionDragMetrics,
  reorderSelection
} from '../utils/reorderSelection'

export function useColumnReorder(
  columnManager,
  selectedColumnIds,
  setSelectedColumnIds
) {
  const [draggedColumnId, setDraggedColumnId] = useState(null)
  const [dropTargetId, setDropTargetId] = useState(null)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const handleDragStart = useCallback(
    (e, columnId) => {
      const col = columnManager.columns.get(columnId)
      if (!col || col.fixed) return

      const nextSelection = selectedColumnIds.includes(columnId)
        ? selectedColumnIds
        : [columnId]

      if (!selectedColumnIds.includes(columnId) && setSelectedColumnIds) {
        setSelectedColumnIds(nextSelection)
      }

      setDraggedColumnId(columnId)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', columnId)

      // Create an empty image to hide the default browser drag ghost
      const emptyImage = new Image()
      emptyImage.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
      e.dataTransfer.setDragImage(emptyImage, 0, 0)

      // Setup custom ghost element
      const th = e.currentTarget
      const thRect = th.getBoundingClientRect()

      const tableExcel = th.closest('table')
      const tableScroll = th.closest('.rp-table-scroll')
      const tableContainer = th.closest('.rp-table-container')
      const thead = tableExcel?.querySelector('thead')

      const scrollRect = tableScroll.getBoundingClientRect()
      const tableRect = tableExcel
        ? tableExcel.getBoundingClientRect()
        : scrollRect
      const theadRect = thead?.getBoundingClientRect()
      const lastColumnFixed = tableExcel.querySelector(
        'th[data-column-id="remain"]'
      )
      const headerElementsById = new Map(
        Array.from(
          tableExcel?.querySelectorAll('th[data-column-id]') || []
        ).map((headerElement) => [
          headerElement.getAttribute('data-column-id'),
          headerElement
        ])
      )
      const selectedRects = nextSelection
        .map((id) => headerElementsById.get(id)?.getBoundingClientRect())
        .filter(Boolean)
      const ghostSize = getSelectionDragMetrics({
        selectedRects,
        fallbackRect: {
          width: thRect.width,
          height: tableRect.height - (theadRect?.height || 0) + thRect.height
        },
        direction: 'horizontal'
      })

      const ghost = document.createElement('div')
      ghost.id = 'custom-column-drag-ghost'
      ghost.style.position = 'absolute'
      ghost.style.width = `${ghostSize.width}px`
      ghost.style.height = `${ghostSize.height}px`
      ghost.style.backgroundColor = 'rgba(0, 0, 0, 0.2)'
      ghost.style.border = '1px solid #d1d5db'
      ghost.style.pointerEvents = 'none'
      ghost.style.zIndex = '1000'
      ghost.style.transition = 'none'
      ghost.style.boxSizing = 'border-box'
      ghost.style.left = '0'
      ghost.style.willChange = 'transform'

      // ghost.innerHTML = `
      //   <div style="padding: 8px 12px; background: #0078d4; color: white; font-weight: bold; font-size: 12px; text-align: center; border-bottom: 1px solid rgba(255,255,255,0.2);">
      //     ${col.label || col.customerCode}
      //   </div>
      // `

      // Render the ghost inside a viewport-sized clip layer (Cách C).
      // The ghost is free to follow the cursor all the way to the last column;
      // whatever spills past the visible area is clipped instead of expanding
      // the table width, so it never gets "stuck" before the real end.
      const containerRect = tableContainer.getBoundingClientRect()
      if (!tableContainer.style.position) {
        tableContainer.style.position = 'relative'
      }

      const ghostClip = document.createElement('div')
      ghostClip.id = 'custom-column-drag-clip'
      ghostClip.style.position = 'absolute'
      ghostClip.style.left = `${scrollRect.left - containerRect.left}px`
      ghostClip.style.top = `${scrollRect.top - containerRect.top}px`
      ghostClip.style.width = `${tableScroll.clientWidth}px`
      ghostClip.style.height = `${tableScroll.clientHeight}px`
      ghostClip.style.overflow = 'hidden'
      ghostClip.style.pointerEvents = 'none'
      ghostClip.style.zIndex = '1000'
      ghostClip.appendChild(ghost)
      tableContainer.appendChild(ghostClip)

      // The ghost is positioned in VIEWPORT coordinates relative to the clip
      // layer (which overlays the scroll viewport). No scrollLeft is added, so
      // it stays under the cursor while the content scrolls beneath it.
      // At drag start it must sit exactly on top of the source selection, so we
      // anchor to the first selected column (not the clicked cell).
      const firstSelectedRect = selectedRects[0] || thRect
      ghost.style.top = `${firstSelectedRect.top - scrollRect.top}px`
      ghost.style.left = '0'

      // Keep the ghost's left edge aligned with the source selection while it
      // follows the cursor horizontally.
      const offsetX = e.clientX - firstSelectedRect.left

      // Left boundary so the ghost never covers the fixed/sticky columns
      const getStickyWidth = () => {
        let width = 0
        const fixedCols = Array.from(
          tableScroll.querySelectorAll(
            'th[data-column-id]:not(.customer-header)'
          )
        )
        if (fixedCols.length > 0) {
          const lastFixed = fixedCols[fixedCols.length - 1]
          const lastFixedRect = lastFixed.getBoundingClientRect()
          width = lastFixedRect.right - scrollRect.left
        }
        return width
      }

      let lastClientX = e.clientX
      const raf = { id: null }

      const updatePosition = (clientX) => {
        lastClientX = clientX

        // Viewport-relative X (no scrollLeft): the ghost tracks the cursor and
        // the clip layer hides whatever spills past the right edge.
        let x = clientX - scrollRect.left - offsetX

        const currentMinX = getStickyWidth()

        // Gentle auto-scroll to the left when hovering near the fixed columns
        if (
          clientX <
          scrollRect.left + lastColumnFixed.getBoundingClientRect().right + 100
        ) {
          tableScroll.scrollLeft -= 10
        }

        // Only clamp the left edge; the right side is intentionally free and
        // clipped by the wrapper so the container never grows.
        if (x < currentMinX) {
          x = currentMinX
        }

        ghost.style.transform = `translateX(${x}px)`
      }

      updatePosition(e.clientX)

      const handleDragOverDoc = (dragEvent) => {
        if (dragEvent.clientX === 0 && dragEvent.clientY === 0) return
        // Capture latest clientX immediately (safe to read outside RAF)
        lastClientX = dragEvent.clientX
        // Cancel any pending frame — only apply the most recent position per frame
        if (raf.id !== null) cancelAnimationFrame(raf.id)
        raf.id = requestAnimationFrame(() => {
          updatePosition(lastClientX)
          raf.id = null
        })
      }

      const handleScroll = () => {
        if (raf.id !== null) cancelAnimationFrame(raf.id)
        raf.id = requestAnimationFrame(() => {
          updatePosition(lastClientX)
          raf.id = null
        })
      }

      document.addEventListener('dragover', handleDragOverDoc)
      tableScroll.addEventListener('scroll', handleScroll)

      // Create source overlay on the dragged column. This one lives inside the
      // scroll content (not the clip layer) so it stays anchored to the
      // original column position and scrolls with the table.
      tableScroll.style.position = 'relative'
      const fixedY = thRect.top - scrollRect.top + tableScroll.scrollTop
      const sourceOverlay = document.createElement('div')
      sourceOverlay.className = 'drag-source-overlay'
      sourceOverlay.style.position = 'absolute'
      sourceOverlay.style.width = `${ghostSize.width}px`
      sourceOverlay.style.height = `${ghostSize.height}px`
      sourceOverlay.style.top = `${fixedY}px`
      sourceOverlay.style.left = `${firstSelectedRect.left - scrollRect.left + tableScroll.scrollLeft}px`
      sourceOverlay.style.pointerEvents = 'none'
      sourceOverlay.style.zIndex = 1
      tableScroll.appendChild(sourceOverlay)

      // Store references on the DOM node for cleanup
      th._dragGhost = ghost
      th._dragClip = ghostClip
      th._dragListener = handleDragOverDoc
      th._scrollListener = handleScroll
      th._sourceOverlay = sourceOverlay
      th._raf = raf

      tableContainer.classList.add('is-dragging')
    },
    [columnManager, selectedColumnIds, setSelectedColumnIds]
  )

  const handleDragEnd = useCallback((e) => {
    setDraggedColumnId(null)
    setDropTargetId(null)
    setIsAtEnd(false)

    const th = e.currentTarget
    if (th._dragClip) {
      if (th._dragClip.parentNode) {
        th._dragClip.parentNode.removeChild(th._dragClip)
      }
      th._dragClip = null
      th._dragGhost = null
    } else if (th._dragGhost) {
      if (th._dragGhost.parentNode) {
        th._dragGhost.parentNode.removeChild(th._dragGhost)
      }
      th._dragGhost = null
    }
    if (th._sourceOverlay) {
      if (th._sourceOverlay.parentNode) {
        th._sourceOverlay.parentNode.removeChild(th._sourceOverlay)
      }
      th._sourceOverlay = null
    }
    if (th._dragListener) {
      document.removeEventListener('dragover', th._dragListener)
      th._dragListener = null
    }
    if (th._scrollListener) {
      const tableScroll = th.closest('.rp-table-scroll')
      if (tableScroll)
        tableScroll.removeEventListener('scroll', th._scrollListener)
      th._scrollListener = null
    }
    // Cancel any pending animation frame to prevent stale DOM mutations
    if (th._raf && th._raf.id !== null) {
      cancelAnimationFrame(th._raf.id)
      th._raf.id = null
      th._raf = null
    }

    const tableContainer = e.currentTarget.closest('.rp-table-container')
    if (tableContainer) {
      tableContainer.classList.remove('is-dragging')
    }
  }, [])

  const handleDragOver = useCallback(
    (e, _passedTargetId) => {
      if (draggedColumnId === null) return

      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'

      // Auto-scroll
      const scrollContainer = e.currentTarget.closest('.rp-table-scroll')
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect()

        let stickyWidth = 0
        const fixedCols = Array.from(
          scrollContainer.querySelectorAll(
            'th[data-column-id]:not(.customer-header)'
          )
        )
        if (fixedCols.length > 0) {
          const lastFixed = fixedCols[fixedCols.length - 1]
          const lastFixedRect = lastFixed.getBoundingClientRect()
          stickyWidth = Math.max(0, lastFixedRect.right - containerRect.left)
        }

        const leftThreshold = 460
        const rightThreshold = 60

        if (e.clientX < containerRect.left + stickyWidth + leftThreshold) {
          const intensity = Math.max(
            1,
            (containerRect.left + stickyWidth + leftThreshold - e.clientX) / 5
          )
          scrollContainer.scrollLeft -= 5 * intensity
        } else if (e.clientX > containerRect.right - rightThreshold) {
          const intensity = Math.max(
            1,
            (e.clientX - (containerRect.right - rightThreshold)) / 5
          )
          scrollContainer.scrollLeft += 5 * intensity
        }
      }

      // Determine target column purely by horizontal mouse position (X coordinate)
      // This allows the highlight to update accurately even if the mouse is hovering over the table body
      const table = e.currentTarget.classList.contains('rp-table-scroll')
        ? e.currentTarget.querySelector('.rp-table')
        : e.currentTarget.closest('.rp-table')

      if (!table) return

      const thElements = Array.from(
        table.querySelectorAll('th[data-column-id]')
      )
      let targetId = null
      let targetRect = null

      for (const th of thElements) {
        const rect = th.getBoundingClientRect()
        // e.clientX within the horizontal bounds of the header cell
        if (e.clientX >= rect.left && e.clientX <= rect.right) {
          targetId = th.getAttribute('data-column-id')
          targetRect = rect
          break
        }
      }

      if (!targetId || !targetRect) return

      const col = columnManager.columns.get(targetId)
      if (!col) return

      if (col.fixed) {
        // If dragging over a fixed column (e.g., when scrolling left),
        // target the first draggable column instead of blocking the drop.
        const firstDraggableId = columnManager.order.find((id) => {
          const c = columnManager.columns.get(id)
          return c && !c.fixed
        })
        if (firstDraggableId) {
          setDropTargetId(firstDraggableId)
          setIsAtEnd(false)
        }
        return
      }

      const x = e.clientX - targetRect.left
      const isRightHalf = x > targetRect.width / 2

      const currentOrder = columnManager.order
      const idx = currentOrder.indexOf(targetId)

      // Prospective insertion index (mirrors the math in handleDrop)
      let nextDropTargetId
      let nextIsAtEnd
      let prospectiveToIndex

      if (isRightHalf) {
        const nextId = currentOrder[idx + 1]
        if (nextId) {
          nextDropTargetId = nextId
          nextIsAtEnd = false
        } else {
          nextDropTargetId = targetId
          nextIsAtEnd = true
        }
        prospectiveToIndex = idx + 1
      } else {
        nextDropTargetId = targetId
        nextIsAtEnd = false
        prospectiveToIndex = idx
      }

      // Suppress the highlight when the drop would land inside the source
      // selection or on either of its edges (it would be a no-op drop).
      const selectedIds = (
        selectedColumnIds.includes(draggedColumnId)
          ? selectedColumnIds
          : [draggedColumnId]
      ).filter((id) => currentOrder.includes(id))
      const selectedIndexes = selectedIds
        .map((id) => currentOrder.indexOf(id))
        .filter((index) => index !== -1)

      if (selectedIndexes.length > 0) {
        const minSelection = Math.min(...selectedIndexes)
        const maxSelection = Math.max(...selectedIndexes)
        if (
          prospectiveToIndex >= minSelection &&
          prospectiveToIndex <= maxSelection + 1
        ) {
          setDropTargetId(null)
          setIsAtEnd(false)
          return
        }
      }

      setDropTargetId(nextDropTargetId)
      setIsAtEnd(nextIsAtEnd)
    },
    [columnManager, draggedColumnId, selectedColumnIds]
  )

  const handleDragLeave = useCallback(() => {
    // We don't clear dropTargetId here because we want it to stay until we enter another cell
  }, [])

  const handleDrop = useCallback(
    (e, targetColumnId) => {
      e.preventDefault()
      const finalTargetId = dropTargetId
      const finalAtEnd = isAtEnd

      setDropTargetId(null)
      setIsAtEnd(false)

      if (!draggedColumnId || !finalTargetId) {
        setDraggedColumnId(null)
        return
      }

      const fromCol = columnManager.columns.get(draggedColumnId)
      if (!fromCol || fromCol.fixed) {
        setDraggedColumnId(null)
        return
      }

      const currentOrder = [...columnManager.order]
      const selectedIds = (
        selectedColumnIds.includes(draggedColumnId)
          ? selectedColumnIds
          : [draggedColumnId]
      ).filter((id) => currentOrder.includes(id))
      const selectedIndexes = selectedIds
        .map((id) => currentOrder.indexOf(id))
        .filter((index) => index !== -1)

      const fromIndex = selectedIndexes[0]
      let toIndex = currentOrder.indexOf(finalTargetId)

      if (fromIndex === undefined || fromIndex === -1 || toIndex === -1) {
        setDraggedColumnId(null)
        return
      }

      if (finalAtEnd) {
        toIndex += 1
      }

      if (selectedIndexes.length > 0) {
        const minSelection = Math.min(...selectedIndexes)
        const maxSelection = Math.max(...selectedIndexes)

        if (toIndex >= minSelection && toIndex <= maxSelection + 1) {
          setDraggedColumnId(null)
          return
        }

        const reorderedOrder = reorderSelection(
          currentOrder,
          selectedIndexes,
          fromIndex,
          toIndex
        )
        columnManager.reorderColumns(reorderedOrder)
      }

      setDraggedColumnId(null)
    },
    [draggedColumnId, columnManager, dropTargetId, isAtEnd, selectedColumnIds]
  )

  return {
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    draggedColumnId,
    dropTargetId,
    isAtEnd
  }
}
